// Cliente HTTP do frontend. Todas as chamadas batem em /api/*, que o
// Vite proxy redireciona para o BFF Go (porta 8090) em dev e que é
// servido pelo mesmo origin em build de produção.
//
// Não tratamos status de erro lançando exceptions: preferimos devolver
// `{ ok, status, data }` para que os componentes possam mostrar a
// resposta bruta de erros 4xx/5xx — afinal, o objetivo do kitchen-sink
// é demonstrar a API, inclusive os caminhos de falha.

export type APIResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
  raw: string;
  /** tempo total do roundtrip em ms, útil pra UI de latência. */
  latencyMs: number;
};

type FetchOpts = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

async function callJSON<T>(path: string, opts: FetchOpts = {}): Promise<APIResult<T>> {
  const method = opts.method ?? "GET";
  const init: RequestInit = {
    method,
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(opts.headers ?? {}),
    },
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.body = typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  }
  const started = performance.now();
  const resp = await fetch(path, init);
  const latencyMs = Math.round(performance.now() - started);
  const text = await resp.text();
  let parsed: T | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as T;
    } catch {
      parsed = null;
    }
  }
  return {
    ok: resp.ok,
    status: resp.status,
    data: parsed,
    raw: text,
    latencyMs,
  };
}

export const api = {
  /** Health do próprio BFF. */
  health: () => callJSON<{ status: string; upstream: string }>("/api/health"),
  /** /health upstream. */
  upstreamHealth: () => callJSON<{ status: string }>("/api/upstream/health"),
  /** /ready upstream. */
  upstreamReady: () => callJSON<Record<string, unknown>>("/api/upstream/ready"),

  /** Lista de arquivos JSON em ../examples. */
  listExamples: () =>
    callJSON<{ examples: { name: string; title: string; summary: string; path: string }[] }>(
      "/api/examples",
    ),
  /** Conteúdo bruto do exemplo. Retorna como string (raw). */
  getExample: (name: string) => callJSON<unknown>(`/api/examples/${encodeURIComponent(name)}`),

  /** POST /v1/route-optimization/jobs. */
  createJob: (payload: unknown, idempotencyKey?: string) =>
    callJSON<unknown>("/api/jobs", {
      method: "POST",
      body: payload,
      headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    }),
  /** GET status. */
  getJob: (id: string) => callJSON<unknown>(`/api/jobs/${encodeURIComponent(id)}`),
  /** GET result. */
  getJobResult: (id: string) => callJSON<unknown>(`/api/jobs/${encodeURIComponent(id)}/result`),
  /** POST cancel. */
  cancelJob: (id: string) =>
    callJSON<unknown>(`/api/jobs/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  /** POST retry. */
  retryJob: (id: string) =>
    callJSON<unknown>(`/api/jobs/${encodeURIComponent(id)}/retry`, { method: "POST" }),

  /** POST /v1/route-optimization/validate. */
  validate: (payload: unknown) =>
    callJSON<unknown>("/api/validate", { method: "POST", body: payload }),

  /** POST /v1/matrix. */
  matrix: (payload: unknown) =>
    callJSON<unknown>("/api/matrix", { method: "POST", body: payload }),
  /** POST /v1/geocode. */
  geocode: (payload: unknown) =>
    callJSON<unknown>("/api/geocode", { method: "POST", body: payload }),
  /** POST /v1/reverse-geocode. */
  reverseGeocode: (payload: unknown) =>
    callJSON<unknown>("/api/reverse-geocode", { method: "POST", body: payload }),

  /** GET /v1/admin/dead-letters — lista paginada com filtros opcionais. */
  listDeadLetters: (params: {
    error_code?: string;
    status?: string;
    since?: string;
    until?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.error_code) qs.set("error_code", params.error_code);
    if (params.status) qs.set("status", params.status);
    if (params.since) qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    const suffix = qs.toString();
    return callJSON<import("../types/routecore").DeadLetterListResponse>(
      suffix ? `/api/dead-letters?${suffix}` : "/api/dead-letters",
    );
  },
  /** GET /v1/admin/dead-letters/:id — detalhe completo. */
  getDeadLetter: (id: string) =>
    callJSON<import("../types/routecore").DeadLetterDetail>(
      `/api/dead-letters/${encodeURIComponent(id)}`,
    ),
  /** POST /v1/admin/dead-letters/:id/replay — re-injeta na fila. */
  replayDeadLetter: (id: string) =>
    callJSON<import("../types/routecore").ReplayResponse>(
      `/api/dead-letters/${encodeURIComponent(id)}/replay`,
      { method: "POST" },
    ),
  /** POST /v1/admin/dead-letters/:id/discard — marca como descartado. */
  discardDeadLetter: (id: string, reason: string) =>
    callJSON<import("../types/routecore").DiscardResponse>(
      `/api/dead-letters/${encodeURIComponent(id)}/discard`,
      { method: "POST", body: { reason } },
    ),
};
